import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";
import EmployeeDetail from "./pages/EmployeeDetail";
import EmployeeForm from "./pages/EmployeeForm";
import Disciplinary from "./pages/Disciplinary";
import Leave from "./pages/Leave";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import PPEManagement from "./pages/PPEManagement";
import PPEDashboard from "./pages/PPEDashboard";
import Kiosk from "./pages/Kiosk";
import AttendanceManagement from "./pages/AttendanceManagement";
import ShiftManagement from "./pages/ShiftManagement";
import EmployeeNotebook from "./pages/EmployeeNotebook";

// Layout
import { Layout } from "./components/layout/Layout";

// Role-based access lists hoisted to module scope so they're stable across
// re-renders (avoids React.memo / context churn from inline arrays).
const KIOSK_ROLES = ['kiosk_user'];
const HR_ROLES = ['admin', 'hr_manager'];
const ADMIN_ROLES = ['admin'];

// Protected Route wrapper
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Master-User-only route (independent of role — checks the is_master flag)
function MasterRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_master) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  // Kiosk users are locked to the kiosk screen
  if (user && user.role === 'kiosk_user') {
    return (
      <Routes>
        <Route path="/kiosk" element={<Kiosk />} />
        <Route path="*" element={<Navigate to="/kiosk" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      
      {/* Kiosk route - accessible by kiosk users only */}
      <Route path="/kiosk" element={
        <ProtectedRoute allowedRoles={KIOSK_ROLES}>
          <Kiosk />
        </ProtectedRoute>
      } />
      
      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/employees" element={<Employees />} />
        <Route path="/employees/new" element={
          <ProtectedRoute allowedRoles={HR_ROLES}>
            <EmployeeForm />
          </ProtectedRoute>
        } />
        <Route path="/employees/:id" element={<EmployeeDetail />} />
        <Route path="/employees/:id/edit" element={
          <ProtectedRoute allowedRoles={HR_ROLES}>
            <EmployeeForm />
          </ProtectedRoute>
        } />
        <Route path="/disciplinary" element={<Disciplinary />} />
        <Route path="/leave" element={<Leave />} />
        <Route path="/notebook" element={
          <MasterRoute>
            <EmployeeNotebook />
          </MasterRoute>
        } />
        <Route path="/attendance" element={
          <ProtectedRoute allowedRoles={HR_ROLES}>
            <AttendanceManagement />
          </ProtectedRoute>
        } />
        <Route path="/shifts" element={
          <ProtectedRoute allowedRoles={HR_ROLES}>
            <ShiftManagement />
          </ProtectedRoute>
        } />
        <Route path="/ppe" element={<PPEDashboard />} />
        <Route path="/ppe/items" element={
          <ProtectedRoute allowedRoles={ADMIN_ROLES}>
            <PPEManagement />
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute allowedRoles={HR_ROLES}>
            <Reports />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute allowedRoles={ADMIN_ROLES}>
            <Settings />
          </ProtectedRoute>
        } />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
